"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {InquiryBookingInfo} from "@/features/rental/inquiry-form/inquiry-form.model";

import {useTranslation} from "@/shared/i18n";
import {DateHelper} from "@/shared/lib";
import {StarRating} from "@/shared/ui/data-display/rating/star-rating";

import "./styles/inquiry-booking-info.scss";

export function BookingInfoBlock({info, unitName}: {info: InquiryBookingInfo; unitName?: string}): JSX.Element {
    const {t} = useTranslation("booking");
    const {t: tReviews} = useTranslation("reviews");

    const formatDate = (iso: string | null): string => {
        const d = iso ? DateHelper.parseLocalDate(iso) : null;
        return d ? DateHelper.formatDateLong(d) : "";
    };

    const formatWeekday = (iso: string | null): string => {
        const d = iso ? DateHelper.parseLocalDate(iso) : null;
        return d ? DateHelper.formatWeekday(d) : "";
    };

    const formatTime = (timeStr?: string): string => DateHelper.formatTime24to12(timeStr);

    const guestParts = [t("adultsCount", {count: info.adults}), t("childrenCount", {count: info.children}), t("petsCount", {count: info.pets})];

    return (
        <div className="inquiry-booking-info">
            <div className="inquiry-booking-info__header">
                {info.unitImage && (
                    <div className="inquiry-booking-info__image">
                        <SmartImage src={info.unitImage} alt={unitName ?? ""} fill style={{objectFit: "cover"}} sizes="88px" />
                    </div>
                )}
                <div className="inquiry-booking-info__meta">
                    {unitName && <p className="inquiry-booking-info__name">{unitName}</p>}
                    {info.unitLocation && <p className="inquiry-booking-info__location">{info.unitLocation}</p>}
                    {info.rating !== undefined && (
                        <div className="inquiry-booking-info__rating">
                            <StarRating name="" rating={info.rating} size="small" />
                            {info.reviewsCount !== undefined && (
                                <span className="inquiry-booking-info__reviews">{tReviews("count", {count: info.reviewsCount})}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {(info.checkIn || info.checkOut) && (
                <div className="inquiry-booking-info__dates">
                    {info.checkIn && (
                        <div className="inquiry-booking-info__date-col">
                            <p className="inquiry-booking-info__date-label">{t("checkIn").toUpperCase()}</p>
                            <p className="inquiry-booking-info__date-weekday">{formatWeekday(info.checkIn)}</p>
                            <p className="inquiry-booking-info__date-value">{formatDate(info.checkIn)}</p>
                            {info.checkInTime && <p className="inquiry-booking-info__date-time">{formatTime(info.checkInTime)}</p>}
                        </div>
                    )}
                    {info.checkOut && (
                        <div className="inquiry-booking-info__date-col">
                            <p className="inquiry-booking-info__date-label">{t("checkOut").toUpperCase()}</p>
                            <p className="inquiry-booking-info__date-weekday">{formatWeekday(info.checkOut)}</p>
                            <p className="inquiry-booking-info__date-value">{formatDate(info.checkOut)}</p>
                            {info.checkOutTime && <p className="inquiry-booking-info__date-time">{formatTime(info.checkOutTime)}</p>}
                        </div>
                    )}
                </div>
            )}

            <div className="inquiry-booking-info__guests-block">
                <p className="inquiry-booking-info__guests-label">{t("guests").toUpperCase()}</p>
                <p className="inquiry-booking-info__guests-value">{guestParts.join(", ")}</p>
            </div>
        </div>
    );
}
